import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import type { CaseStudyContent } from "../../data/caseStudies";
import { CaseStudySection } from "./CaseStudySection";
import { BODY, EYEBROW, H2, H3 } from "../../utils/typography";
type FeaturesSectionProps = {
  features: NonNullable<CaseStudyContent["features"]>;
};
const ease = [0.23, 1, 0.32, 1] as const;

/** Headline feature gets a dark panel; supporting features sit in a quiet two-column list. */
export function FeaturesSection({
  features
}: FeaturesSectionProps) {
  const {
    lead,
    items
  } = features;
  return <CaseStudySection label={features.heading} data-id="element-70" data-mp="fiy9dh7tsgez">
      <article className="grid grid-cols-1 gap-10 rounded-3xl bg-espresso p-6 md:p-10 xl:grid-cols-[minmax(0,1fr)_18rem] xl:items-center" data-id="element-71" data-mp="fjpsvto5enow">
        <div data-id="element-72" data-mp="5l0l4tpyujmd">
          <p className={`${EYEBROW} text-mustard`} data-id="element-73" data-mp="5ljtbiptdtn7">{lead.eyebrow}</p>
          <h3 className={`mt-4 ${H2} text-cream`} data-id="element-74" data-mp="5muwphfckzds">{lead.title}</h3>
          <div className="mt-5 space-y-4" data-id="element-75" data-mp="mh1izyuhjx6">
            {lead.body.map(p => <p key={p} className={`${BODY} text-cream/80`} data-id="element-76" data-mp="1upcf2af082rl">
                {p}
              </p>)}
          </div>
        </div>
        <ChatIllustration data-id="element-77" data-mp="1zj6tozfr5rjt" />
      </article>

      <ul className="mt-14 grid grid-cols-1 gap-x-10 gap-y-10 md:grid-cols-2" data-id="element-78" data-mp="1kszc0lxkkck6">
        {items.map(item => <li key={item.title} data-id="element-79" data-mp="1psc8lye8sxzy">
            <h3 className={`${H3} text-espresso`} data-id="element-80" data-mp="1pp6jij5m5w48">{item.title}</h3>
            <p className={`mt-3 ${BODY} text-espresso/70`} data-id="element-81" data-mp="2dhwr6g6hht8p">{item.body}</p>
          </li>)}
      </ul>
    </CaseStudySection>;
}

/** Abstract chat exchange: a question, a typing pause, then an answer. Decorative only. */
function ChatIllustration() {
  const reduce = useReducedMotion();
  const appear = (delay: number) => reduce ? {} : {
    initial: {
      opacity: 0,
      y: 8,
      scale: 0.96
    },
    whileInView: {
      opacity: 1,
      y: 0,
      scale: 1
    },
    viewport: {
      once: true,
      amount: 0.6
    },
    transition: {
      duration: 0.25,
      ease,
      delay
    }
  };
  return <div aria-hidden="true" className="flex flex-col gap-3 rounded-2xl bg-bark p-5" data-id="element-82" data-mp="2znz98vu07zgz">
      <motion.div {...appear(0.1)} className="ml-auto w-3/4 space-y-2 rounded-2xl rounded-br-md bg-cream/15 px-4 py-3" data-id="element-83" data-mp="2upxtokvseia9">
        <span className="block h-2 w-full rounded-full bg-cream/50" data-id="element-84" data-mp="2pxwil2acuxlt" />
        <span className="block h-2 w-2/3 rounded-full bg-cream/50" data-id="element-85" data-mp="2l163ah6dk61g" />
      </motion.div>

      <motion.div {...appear(0.45)} className="flex w-16 items-center justify-center gap-1 rounded-2xl rounded-bl-md bg-mustard/90 px-3 py-3" data-id="element-86" data-mp="2g1t5j1dytek3">
        {[0, 1, 2].map(i => <motion.span key={i} className="h-1.5 w-1.5 rounded-full bg-espresso" animate={reduce ? undefined : {
        opacity: [0.3, 1, 0.3]
      }} transition={{
        duration: 0.9,
        repeat: Infinity,
        delay: i * 0.15
      }} data-id="element-87" data-mp="2c22gb24376ho" />)}
      </motion.div>

      <motion.div {...appear(0.9)} className="w-5/6 space-y-2 rounded-2xl rounded-bl-md bg-mustard px-4 py-3" data-id="element-88" data-mp="56w2ox7lci5p">
        <span className="block h-2 w-full rounded-full bg-espresso/60" data-id="element-89" data-mp="7toaoujw05zq" />
        <span className="block h-2 w-full rounded-full bg-espresso/60" data-id="element-90" data-mp="7rlnsje16sz8" />
        <span className="block h-2 w-1/2 rounded-full bg-espresso/60" data-id="element-91" data-mp="crgd4ii5rai6" />
      </motion.div>
    </div>;
}